import type { DocStatus, BrandConfig } from '../../../types/docops';

// ── BRAND PALETTE (light theme — matches landing page) ──
export const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  accent: '#E11D48', accentSoft: '#FFF1F2',
  bg: '#FFFFFF', bgSoft: '#F8FAFC',
  border: '#E4E4E7', borderLight: '#F4F4F5',
  btn: '#18181B', btnHover: '#27272A',
  success: '#059669', successBg: '#D1FAE5',
  amber: '#D97706', amberBg: '#FEF3C7',
  red: '#DC2626', redBg: '#FEF2F2',
  orange: '#EA580C', orangeBg: '#FFF7ED',
  blue: '#2563EB', blueBg: '#DBEAFE',
};

// ── STATUS CONFIG ──
export const STATUS_CONFIG: Record<DocStatus, { label: string; color: string; bg: string }> = {
  not_started:       { label: 'NOT STARTED',    color: C.textMuted, bg: C.borderLight },
  generating:        { label: 'GENERATING',     color: C.blue,      bg: C.blueBg },
  draft:             { label: 'DRAFT',          color: C.amber,     bg: C.amberBg },
  attorney_review:   { label: 'ATTY REVIEW',    color: C.orange,    bg: C.orangeBg },
  attorney_approved: { label: 'APPROVED',       color: C.blue,      bg: C.blueBg },
  final:             { label: 'FINAL',          color: C.success,   bg: C.successBg },
  signed:            { label: 'SIGNED',         color: C.success,   bg: C.successBg },
  expired:           { label: 'EXPIRED',        color: C.red,       bg: C.redBg },
  needs_renewal:     { label: 'RENEWAL',        color: C.orange,    bg: C.orangeBg },
};

export const ALL_STATUSES: DocStatus[] = [
  'not_started', 'generating', 'draft', 'attorney_review',
  'attorney_approved', 'final', 'signed', 'expired', 'needs_renewal',
];

// ── PIPELINE STAGES ──
export interface PipelineStage {
  id: string;
  label: string;
  color: string;
  dot: string;
  statuses: DocStatus[];
  description: string;
}

export const PIPELINE_STAGES: PipelineStage[] = [
  { id: 'needs_generation', label: 'Needs Generation', color: C.textMuted, dot: '#D4D4D8', statuses: ['not_started'], description: 'Templates awaiting AI generation' },
  { id: 'in_progress', label: 'Generating', color: C.blue, dot: C.blue, statuses: ['generating'], description: 'Currently being generated by AI' },
  { id: 'ready_for_review', label: 'Ready for Review', color: C.amber, dot: C.amber, statuses: ['draft'], description: 'Generated — review and promote to attorney' },
  { id: 'attorney_review', label: 'Attorney Review', color: C.orange, dot: C.orange, statuses: ['attorney_review'], description: 'With attorney for legal review' },
  { id: 'approved', label: 'Complete', color: C.success, dot: C.success, statuses: ['attorney_approved', 'final', 'signed', 'expired', 'needs_renewal'], description: 'Approved, finalized, or executed' },
];

// ── CONFLICT SEVERITY CONFIG ──
export const SEVERITY_CONFIG = {
  critical: { label: 'Critical', color: C.red, bg: C.redBg, border: '#FECACA' },
  high:     { label: 'High',     color: C.orange, bg: C.orangeBg, border: '#FED7AA' },
  medium:   { label: 'Medium',   color: C.amber, bg: C.amberBg, border: '#FDE68A' },
  low:      { label: 'Low',      color: C.blue, bg: C.blueBg, border: '#BFDBFE' },
};

// ── BRAND CONFIG SECTIONS ──
export interface ConfigSection {
  key: string;
  label: string;
  icon: string;
  fields: { key: keyof BrandConfig; label: string; type: 'text' | 'number' | 'array' }[];
}

export const CONFIG_SECTIONS: ConfigSection[] = [
  {
    key: 'brand', label: 'Brand Info', icon: '\uD83C\uDFE2',
    fields: [
      { key: 'brandName', label: 'Brand Name', type: 'text' },
      { key: 'brandDomain', label: 'Brand Domain', type: 'text' },
      { key: 'brandEntity', label: 'Brand Entity', type: 'text' },
      { key: 'stateOfFormation', label: 'State of Formation', type: 'text' },
      { key: 'ein', label: 'EIN', type: 'text' },
      { key: 'marketVertical', label: 'Market Vertical', type: 'text' },
      { key: 'productType', label: 'Product Type', type: 'text' },
      { key: 'effectiveDate', label: 'Effective Date', type: 'text' },
    ],
  },
  {
    key: 'people', label: 'People', icon: '\uD83D\uDC65',
    fields: [
      { key: 'ceoName', label: 'CEO Name', type: 'text' },
      { key: 'ceoEmail', label: 'CEO Email', type: 'text' },
      { key: 'medicalDirectorName', label: 'Medical Director Name', type: 'text' },
      { key: 'mdCredentials', label: 'MD Credentials', type: 'text' },
      { key: 'mdLicenseStates', label: 'MD License States (comma-separated)', type: 'array' },
      { key: 'pcEntityName', label: 'PC Entity Name', type: 'text' },
      { key: 'pcOwnerName', label: 'PC Owner Name', type: 'text' },
      { key: 'trustName', label: 'Trust Name', type: 'text' },
      { key: 'trustTrusteeName', label: 'Trust Trustee Name', type: 'text' },
    ],
  },
  {
    key: 'partners', label: 'Partners', icon: '\uD83E\uDD1D',
    fields: [
      { key: 'telehealthNetwork', label: 'Telehealth Network', type: 'text' },
      { key: 'pharmacyPartner', label: 'Pharmacy Partner', type: 'text' },
      { key: 'paymentProcessor', label: 'Payment Processor', type: 'text' },
      { key: 'fulfillmentPartner', label: 'Fulfillment Partner', type: 'text' },
      { key: 'cloudProvider', label: 'Cloud Provider', type: 'text' },
    ],
  },
  {
    key: 'financials', label: 'Financials', icon: '\uD83D\uDCB0',
    fields: [
      { key: 'consultationFee', label: 'Consultation Fee', type: 'number' },
      { key: 'telehealthCost', label: 'Telehealth Cost', type: 'number' },
      { key: 'pcRetainer', label: 'PC Retainer', type: 'number' },
      { key: 'mdRetainer', label: 'MD Retainer', type: 'number' },
      { key: 'ipLicenseRoyaltyPercent', label: 'IP License Royalty %', type: 'number' },
      { key: 'supplementMarginPercent', label: 'Supplement Margin %', type: 'text' },
    ],
  },
  {
    key: 'contacts', label: 'Contacts', icon: '\uD83D\uDCE7',
    fields: [
      { key: 'legalEmail', label: 'Legal Email', type: 'text' },
      { key: 'privacyEmail', label: 'Privacy Email', type: 'text' },
      { key: 'supportEmail', label: 'Support Email', type: 'text' },
      { key: 'complianceEmail', label: 'Compliance Email', type: 'text' },
      { key: 'dmcaEmail', label: 'DMCA Email', type: 'text' },
    ],
  },
  {
    key: 'addresses', label: 'Addresses & Jurisdictions', icon: '\uD83C\uDFE0',
    fields: [
      { key: 'businessAddress', label: 'Business Address', type: 'text' },
      { key: 'registeredAgentAddress', label: 'Registered Agent Address', type: 'text' },
      { key: 'arbitrationCounty', label: 'Arbitration County', type: 'text' },
      { key: 'jurisdictions', label: 'Jurisdictions (comma-separated state codes)', type: 'array' },
    ],
  },
  {
    key: 'documents', label: 'Document Settings', icon: '\uD83D\uDCC4',
    fields: [
      { key: 'lastUpdatedDate', label: 'Last Updated Date', type: 'text' },
      { key: 'tier1Price', label: 'Tier 1 (AI Analysis) Price', type: 'text' },
      { key: 'tier1PriceDescription', label: 'Tier 1 Pricing Description', type: 'text' },
      { key: 'privacyPolicyUrl', label: 'Privacy Policy URL', type: 'text' },
      { key: 'chdPolicyUrl', label: 'Consumer Health Data Policy URL', type: 'text' },
      { key: 'hipaaNoticeUrl', label: 'HIPAA NPP URL', type: 'text' },
      { key: 'telehealthConsentUrl', label: 'Telehealth Consent URL', type: 'text' },
      { key: 'cookiePolicyUrl', label: 'Cookie Policy URL', type: 'text' },
    ],
  },
];

// ── FIELD LABELS (flat map: BrandConfig key -> human label) ──
export const FIELD_LABELS: Record<string, string> = Object.fromEntries(
  CONFIG_SECTIONS.flatMap(s => s.fields.map(f => [f.key, f.label])),
);

// ── CATEGORY INFO (for attorney review grouping) ──
export const CATEGORY_INFO: Record<string, { name: string; icon: string; color: string; order: number }> = {
  'user-facing-legal':     { name: 'User-Facing Legal',          icon: '\uD83D\uDCDC', color: '#8B0000', order: 1 },
  'corporate-formation':   { name: 'Corporate / Entity',         icon: '\uD83C\uDFDB\uFE0F', color: '#2F4F4F', order: 2 },
  'trust-ip':              { name: 'Trust & IP Licensing',       icon: '\uD83D\uDD10', color: '#4B0082', order: 3 },
  'mso-pc':                { name: 'MSO / PC Structure',         icon: '\uD83C\uDFE5', color: '#006400', order: 4 },
  'hipaa':                 { name: 'HIPAA Compliance',           icon: '\uD83D\uDEE1\uFE0F', color: '#8B4513', order: 5 },
  'vendor-partner':        { name: 'Vendor / Partner',           icon: '\uD83E\uDD1D', color: '#191970', order: 6 },
  'employment-contractor': { name: 'Employment / Contractor',    icon: '\uD83D\uDC65', color: '#556B2F', order: 7 },
  'compliance-ops':        { name: 'Compliance Operations',      icon: '\u2699\uFE0F', color: '#800020', order: 8 },
  'dashboards-tools':      { name: 'Dashboards & Tools',        icon: '\uD83D\uDCCA', color: '#2E8B57', order: 9 },
  'business-planning':     { name: 'Business Planning',          icon: '\uD83D\uDCC8', color: '#4169E1', order: 10 },
  'acquisition-dataroom':  { name: 'Acquisition / Data Room',   icon: '\uD83D\uDCB0', color: '#DAA520', order: 11 },
  'clinical':              { name: 'Clinical',                   icon: '\uD83E\uDDEC', color: '#DC143C', order: 12 },
  'soc2-infosec':          { name: 'SOC 2 / Info Security',     icon: '\uD83D\uDD12', color: '#1a5276', order: 13 },
};

// ── GENERATION STATUS MESSAGES ──
export const GEN_MESSAGES = [
  'Analyzing document blueprint...',
  'Loading mandatory provisions...',
  'Reviewing regulatory frameworks...',
  'Drafting legal provisions...',
  'Cross-referencing compliance standards...',
  'Structuring document sections...',
  'Incorporating industry best practices...',
  'Reviewing HIPAA requirements...',
  'Checking state-specific regulations...',
  'Building anti-kickback safe harbors...',
  'Reviewing corporate practice of medicine doctrine...',
  'Drafting indemnification clauses...',
  'Structuring assignment provisions...',
  'Reviewing IP ownership terms...',
  'Running quality gate scorer...',
  'Validating mandatory provisions...',
  'Checking keyword coverage...',
  'Almost there... polishing language...',
  'Finalizing document...',
];
